import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useCurrency } from '../../context/CurrencyContext';
import { fetchActivePlans } from '../../services/plansService';

const {
  FiX, FiCheckCircle, FiZap, FiShield, FiStar, FiArrowRight,
  FiRefreshCw, FiUsers, FiFileText, FiLock,
  FiPhone, FiAlertCircle, FiCpu, FiTrendingUp, FiLoader,
  FiPackage, FiActivity, FiGlobe,
} = FiIcons;

// Map plan_key → static UI metadata
const PLAN_META = {
  will_draft: {
    icon: FiFileText,
    iconBg: 'bg-blue-50 text-blue-500',
    cta: 'Get My Draft',
    ctaStyle: 'border-2 border-accent-navy text-accent-navy hover:bg-accent-navy hover:text-white',
    highlight: false,
    badge: null,
  },
  will_guardian: {
    icon: FiShield,
    iconBg: 'bg-accent-gold/10 text-accent-gold',
    cta: 'Start Guardian Plan',
    ctaStyle: 'bg-accent-gold text-white hover:bg-accent-gold/90 shadow-xl shadow-accent-gold/30',
    highlight: true,
    badge: 'Most Popular',
  },
  will_estate: {
    icon: FiStar,
    iconBg: 'bg-purple-50 text-purple-500',
    cta: 'Start Concierge Plan',
    ctaStyle: 'border-2 border-accent-navy text-accent-navy hover:bg-accent-navy hover:text-white',
    highlight: false,
    badge: 'Full Protection',
  },
};

const FALLBACK_PLANS = [
  {
    id: 'will_draft', plan_key: 'will_draft', name: 'Will Draft',
    description: 'One-time generation', price_usd: 29, billing_cycle: 'one-time',
    features: ['Full AI-guided 4-step interview', 'Jurisdiction-aware legal language (30+ countries)', 'Downloadable PDF will draft', 'Executor & beneficiary clauses', 'Legal disclaimer & next-steps guide'],
    is_active: true, sort_order: 10,
  },
  {
    id: 'will_guardian', plan_key: 'will_guardian', name: 'Legacy Guardian',
    description: 'Ongoing will management', price_usd: 15, billing_cycle: 'month',
    features: ['Everything in Will Draft', 'Unlimited will updates & revisions', 'Annual legal check-in reminders', 'Life-event alerts', 'Digital asset vault (up to 50 entries)', '1x attorney consultation per year', 'Beneficiary notification service', 'Priority email & chat support'],
    is_active: true, sort_order: 11,
  },
  {
    id: 'will_estate', plan_key: 'will_estate', name: 'Estate Concierge',
    description: 'Complete estate management', price_usd: 39, billing_cycle: 'month',
    features: ['Everything in Legacy Guardian', 'Unlimited digital asset vault entries', 'Multi-jurisdiction document management', 'Dedicated estate concierge (human)', 'Quarterly attorney reviews', 'Family member sub-accounts (up to 4)', 'Trust & power of attorney drafting', 'Probate assistance & filing guidance', 'White-glove document notarisation service'],
    is_active: true, sort_order: 12,
  },
];

// ── Feature Row ───────────────────────────────────────────────────────────
const FeatureRow = ({ text, included }) => (
  <li className="flex items-start gap-3">
    <SafeIcon
      icon={included ? FiCheckCircle : FiX}
      className={`w-4 h-4 flex-shrink-0 mt-0.5 ${included ? 'text-green-500' : 'text-brand-dark'}`}
    />
    <span className={`text-xs leading-relaxed ${included ? 'text-accent-navy font-medium' : 'text-text-muted line-through opacity-60'}`}>
      {text}
    </span>
  </li>
);

// ── Plan Card ─────────────────────────────────────────────────────────────
const PlanCard = ({ plan, meta, billing, fmtPrice, onSelect, selected, currency }) => {
  const isOneTime = plan.billing_cycle === 'one-time' || plan.billing_cycle === 'forever';
  // For monthly plans: show annual price as 80% if billing=annual (20% discount)
  const displayPrice = (!isOneTime && billing === 'annual')
    ? plan.price_usd * 0.8
    : plan.price_usd;
  const isNonUSD = currency !== 'USD';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative flex flex-col rounded-3xl border-2 overflow-hidden transition-all duration-300 ${
        meta.highlight
          ? 'border-accent-gold shadow-2xl shadow-accent-gold/20 scale-[1.02]'
          : selected === plan.plan_key
          ? 'border-accent-navy shadow-xl'
          : 'border-brand-dark hover:border-accent-navy/40 hover:shadow-lg'
      }`}
    >
      {meta.badge && (
        <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${
          meta.highlight ? 'bg-accent-gold text-white' : 'bg-purple-100 text-purple-600'
        }`}>
          {meta.badge}
        </div>
      )}

      <div className={`p-6 ${meta.highlight ? 'bg-gradient-to-br from-accent-navy to-[#1a2a5e]' : 'bg-white'}`}>
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 ${
          meta.highlight ? 'bg-accent-gold/20 text-accent-gold' : meta.iconBg
        }`}>
          <SafeIcon icon={meta.icon} className="w-6 h-6" />
        </div>

        <p className={`text-[9px] font-black uppercase tracking-widest mb-1 ${meta.highlight ? 'text-accent-gold' : 'text-text-muted'}`}>
          {plan.description}
        </p>
        <h3 className={`text-xl font-serif mb-4 ${meta.highlight ? 'text-white' : 'text-accent-navy'}`}>
          {plan.name}
        </h3>

        {/* ── Price block ── */}
        <div className="flex items-end gap-1.5 mb-1 flex-wrap">
          <span className={`text-4xl font-black font-serif ${meta.highlight ? 'text-white' : 'text-accent-navy'}`}>
            {fmtPrice(displayPrice)}
          </span>
          {!isOneTime && (
            <span className={`text-sm font-light mb-1 ${meta.highlight ? 'text-white/60' : 'text-text-muted'}`}>
              /mo
            </span>
          )}
        </div>

        {/* USD reference when non-USD */}
        {isNonUSD && displayPrice > 0 && (
          <p className={`text-[9px] font-light mb-1 ${meta.highlight ? 'text-white/40' : 'text-text-muted'}`}>
            ${displayPrice.toFixed(2)} USD
          </p>
        )}

        <p className={`text-[10px] font-light mb-5 ${meta.highlight ? 'text-white/50' : 'text-text-muted'}`}>
          {isOneTime ? 'one-time payment' : billing === 'annual' ? 'billed annually' : 'billed monthly'}
        </p>

        {!isOneTime && billing === 'annual' && (
          <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest mb-5 ${
            meta.highlight ? 'bg-accent-gold/20 text-accent-gold' : 'bg-green-50 text-green-600'
          }`}>
            <SafeIcon icon={FiTrendingUp} className="w-3 h-3" />
            Save ${((plan.price_usd - displayPrice) * 12).toFixed(0)}/yr
          </div>
        )}

        <button
          onClick={() => onSelect(plan.plan_key)}
          className={`w-full py-3.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${meta.ctaStyle}`}
        >
          {meta.cta} <SafeIcon icon={FiArrowRight} className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="flex-1 p-6 bg-brand-light border-t border-brand-dark">
        <ul className="space-y-3">
          {(plan.features || []).map((feat, i) => (
            <FeatureRow key={i} text={feat} included={true} />
          ))}
        </ul>
      </div>
    </motion.div>
  );
};

// ── Main Modal ─────────────────────────────────────────────────────────────
const WillPricingModal = ({ isOpen, onClose, onSelectPlan }) => {
  const { formatCurrency, convertAmount, currency, getExchangeRate, ratesReady } = useCurrency();
  const [billing,  setBilling]  = useState('annual');
  const [selected, setSelected] = useState(null);
  const [plans,    setPlans]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  // Convert USD → display currency
  const fmtPrice = useCallback((usd) => {
    if (usd === 0) return 'Free';
    return formatCurrency(convertAmount(usd, 'USD'));
  }, [formatCurrency, convertAmount]);

  const loadPlans = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const all = await fetchActivePlans();
      const willPlans = (Array.isArray(all) ? all : [])
        .filter(p => p.plan_key && p.plan_key.startsWith('will_'))
        .sort((a, b) => a.sort_order - b.sort_order);
      setPlans(willPlans.length > 0 ? willPlans : FALLBACK_PLANS);
    } catch {
      setPlans(FALLBACK_PLANS);
      setError('Using default plans — could not reach server.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) loadPlans();
  }, [isOpen, loadPlans]);

  const handleSelect = (planKey) => {
    setSelected(planKey);
    setTimeout(() => {
      onSelectPlan?.(planKey);
      onClose();
    }, 300);
  };

  if (!isOpen) return null;

  const isNonUSD = currency !== 'USD';
  const rate = getExchangeRate('USD', currency);
  const rateStr = rate >= 1000
    ? rate.toLocaleString(undefined, { maximumFractionDigits: 0 })
    : rate.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[110] flex items-center justify-center p-4 overflow-y-auto"
        onClick={e => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 24 }}
          transition={{ type: 'spring', stiffness: 280, damping: 28 }}
          className="bg-white w-full max-w-5xl rounded-[32px] shadow-2xl overflow-hidden my-4"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-accent-navy to-[#1a2a5e] px-8 py-7 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 bg-accent-gold rounded-2xl flex items-center justify-center shadow-lg shadow-accent-gold/30">
                <SafeIcon icon={FiCpu} className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-[9px] font-black uppercase tracking-widest text-accent-gold mb-0.5">
                  AI Will Drafting Service
                </p>
                <h2 className="text-xl font-serif text-white">Choose Your Plan</h2>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Live rate indicator */}
              {isNonUSD && (
                <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded-xl border border-white/20">
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${ratesReady ? 'bg-green-400 animate-pulse' : 'bg-amber-400'}`} />
                  <span className="text-[9px] font-bold text-white/80 uppercase tracking-widest">
                    1 USD = {rateStr} {currency}
                  </span>
                </div>
              )}
              <button onClick={loadPlans} title="Refresh plans" className="w-9 h-9 bg-white/10 hover:bg-white/20 rounded-xl flex items-center justify-center transition-all">
                <SafeIcon icon={loading ? FiLoader : FiRefreshCw} className={`w-4 h-4 text-white ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button onClick={onClose} className="w-9 h-9 bg-white/10 hover:bg-white/20 rounded-xl flex items-center justify-center transition-all">
                <SafeIcon icon={FiX} className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* Currency notice */}
          {isNonUSD && (
            <div className="px-8 pt-4">
              <div className="flex items-center gap-3 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-xl">
                <SafeIcon icon={FiGlobe} className="w-4 h-4 text-amber-600 flex-shrink-0" />
                <p className="text-[11px] text-amber-800 font-medium flex-1">
                  Prices shown in <strong>{currency}</strong> — converted from USD using{' '}
                  <span className={`font-bold ${ratesReady ? 'text-green-700' : 'text-amber-700'}`}>
                    {ratesReady ? 'live exchange rates' : 'estimated rates'}
                  </span>.
                  Plans are billed in USD.
                </p>
                <span className={`flex-shrink-0 text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-lg border ${ratesReady ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-200 text-amber-800 border-amber-300'}`}>
                  {ratesReady ? '● Live' : '◌ Est.'}
                </span>
              </div>
            </div>
          )}

          {/* Billing Toggle */}
          <div className="px-8 pt-6 pb-2 flex flex-col items-center gap-3">
            <p className="text-center text-text-muted text-sm font-light max-w-xl">
              Protect your family's future with a legally-structured will draft — tailored to your jurisdiction.
            </p>
            <div className="flex items-center gap-3 bg-brand-light border border-brand-dark rounded-2xl p-1.5 mt-2">
              {['monthly', 'annual'].map(b => (
                <button
                  key={b}
                  onClick={() => setBilling(b)}
                  className={`px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                    billing === b
                      ? 'bg-accent-navy text-white shadow-lg shadow-accent-navy/20'
                      : 'text-text-muted hover:text-accent-navy'
                  }`}
                >
                  {b === 'monthly' ? 'Monthly' : 'Annual · Save 20%'}
                </button>
              ))}
            </div>
          </div>

          {/* Plans Grid */}
          <div className="px-8 py-8">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16 gap-4">
                <div className="w-12 h-12 border-4 border-accent-gold/20 border-t-accent-gold rounded-full animate-spin" />
                <p className="text-text-muted text-sm font-bold uppercase tracking-widest">Loading plans…</p>
              </div>
            ) : (
              <>
                {error && (
                  <div className="mb-4 flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                    <SafeIcon icon={FiAlertCircle} className="w-4 h-4 text-amber-500 flex-shrink-0" />
                    <p className="text-xs text-amber-700">{error}</p>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {plans.map(plan => {
                    const meta = PLAN_META[plan.plan_key] || {
                      icon: FiPackage,
                      iconBg: 'bg-gray-50 text-gray-500',
                      cta: 'Select Plan',
                      ctaStyle: 'bg-accent-navy text-white hover:bg-accent-navy/90',
                      highlight: plan.highlight || false,
                      badge: null,
                    };
                    return (
                      <PlanCard
                        key={plan.id || plan.plan_key}
                        plan={plan}
                        meta={meta}
                        billing={billing}
                        fmtPrice={fmtPrice}
                        onSelect={handleSelect}
                        selected={selected}
                        currency={currency}
                      />
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* Trust Badges */}
          <div className="px-8 pb-8">
            <div className="bg-brand-light border border-brand-dark rounded-2xl p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { icon: FiLock,      label: 'AES-256 Encrypted', sub: 'Bank-grade security'        },
                  { icon: FiRefreshCw, label: 'Cancel Anytime',    sub: 'No lock-in contracts'       },
                  { icon: FiUsers,     label: '30+ Jurisdictions', sub: 'Global legal coverage'      },
                  { icon: FiPhone,     label: '24/7 Support',      sub: 'Guardian & Concierge plans' },
                ].map(b => (
                  <div key={b.label} className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-accent-navy/5 text-accent-navy rounded-xl flex items-center justify-center flex-shrink-0">
                      <SafeIcon icon={b.icon} className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-[10px] font-black text-accent-navy">{b.label}</p>
                      <p className="text-[9px] text-text-muted font-light">{b.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-start gap-3 mt-4 p-4 bg-amber-50 border border-amber-100 rounded-xl">
              <SafeIcon icon={FiAlertCircle} className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-amber-700 font-light leading-relaxed">
                All plans generate a <strong>draft template only</strong>. Documents must be reviewed by a licensed attorney in your jurisdiction before they are legally valid. Subscription plans include attorney consultation minutes — not full legal representation.
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default WillPricingModal;